<template>
    <div
        class="answer-products"
        :class="{ 'draft-mode': isDraftModeEnabled }"
    >
        <template v-for="(product, index) in products">
            <div
                v-if="product.id !== 'spacer'"
                :key="product.id"
                :ref="(el) => setItemRef(el, index)"
                class="answer-product"
                :class="[
                    `answer-product-${index + 1}`,
                    {
                        placed: product.isPlaced,
                        dragging: draggedProduct === product.id,
                        wide: productSizeClassById[product.id] === 'wide',
                        tall: productSizeClassById[product.id] === 'tall'
                    }
                ]"
                :draggable="!product.isPlaced"
                @dragstart="handleDragStart($event, product.id)"
                @dragend="handleDragEnd"
                @touchstart="handleTouchStart($event, product)"
                @touchmove="handleTouchMove"
                @touchend="handleTouchEnd"
            >
                <img
                    v-if="product.name && product.image"
                    :src="product.image"
                    :alt="product.name"
                    @load="($event: Event) => onImageLoad($event, product.id)"
                />
            </div>
            <div
                v-else
                :key="`spacer-${index}`"
                class="answer-product spacer"
            />
        </template>
    </div>
</template>

<script setup lang="ts">
    import type { Product } from '../../types/quiz';

    const props = defineProps<{
        products: Product[];
        draggedProduct: string | null;
    }>();

    const emit = defineEmits<{
        dragStart: [productId: string, event: DragEvent | TouchEvent];
        dragEnd: [];
        touchMove: [event: TouchEvent];
        touchDrop: [event: TouchEvent];
    }>();

    const { isDraftModeEnabled } = useDraftMode();

    const itemEls = ref<(HTMLDivElement | null)[]>([]);
    const productSizeClassById = ref<Record<string, 'wide' | 'tall' | null>>({});

    watch(
        () => props.products.map((p) => p.id),
        (productIds) => {
            itemEls.value = props.products.map(() => null);
            const productIdSet = new Set(productIds);
            productSizeClassById.value = Object.fromEntries(
                Object.entries(productSizeClassById.value).filter(([productId]) => productIdSet.has(productId))
            ) as Record<string, 'wide' | 'tall' | null>;
            classifyFromRefs();
        },
        { immediate: true }
    );

    function setItemRef(el: Element | ComponentPublicInstance | null, index: number) {
        if (el instanceof HTMLDivElement) {
            itemEls.value[index] = el;
            return;
        }
        if (el === null) {
            itemEls.value[index] = null;
        }
    }

    function getSizeClass(wrapper: HTMLDivElement | null | undefined): 'wide' | 'tall' | null {
        if (!wrapper) return null;

        const img = wrapper.querySelector('img');
        if (!img || !img.naturalWidth) return null;

        const ratio = img.naturalWidth / img.naturalHeight;

        if (ratio > 1.5) return 'wide';
        if (ratio < 0.75) return 'tall';
        return null;
    }

    function onImageLoad(event: Event, productId: string) {
        const img = event.target as HTMLImageElement;
        productSizeClassById.value[productId] = getSizeClass(img.parentElement as HTMLDivElement);
    }

    function classifyFromRefs() {
        nextTick(() => {
            for (const [index, el] of itemEls.value.entries()) {
                if (!el) continue;
                const product = props.products[index];
                if (!product || product.id === 'spacer') continue;
                productSizeClassById.value[product.id] = getSizeClass(el);
            }
        });
    }

    onMounted(() => {
        classifyFromRefs();
    });

    const handleDragStart = (event: DragEvent, productId: string) => {
        emit('dragStart', productId, event);
    };

    const handleDragEnd = () => {
        emit('dragEnd');
    };

    const handleTouchStart = (event: TouchEvent, product: Product) => {
        if (product.isPlaced) return;

        event.preventDefault();

        const target = event.currentTarget as HTMLElement;
        target.style.transition = 'none';

        emit('dragStart', product.id, event);
    };

    const handleTouchMove = (event: TouchEvent) => {
        event.preventDefault();
        emit('touchMove', event);
    };

    const handleTouchEnd = (event: TouchEvent) => {
        emit('touchDrop', event);
    };
</script>

<style scoped lang="scss">
    .answer-products {
        position: absolute;
        bottom: 25%;
        left: 2%;
        right: 2%;
        height: 13%;
        z-index: 15;
        display: grid;
        grid-template-columns: repeat(20, 1fr);
        grid-auto-rows: 50%;
        grid-auto-flow: dense;
    }

    .answer-product {
        position: relative;

        display: flex;
        justify-content: center;
        align-items: flex-end;
        grid-column: span 4;

        cursor: grab;
        transition: all 0.3s ease;
        filter: drop-shadow(0 4px 8px rgba(0, 0, 0, 0.2));
        -webkit-user-select: none;
        user-select: none;
        -webkit-touch-callout: none;
        touch-action: none;

        img {
            width: 100%;
            height: auto;
            display: block;
            object-fit: contain;
            margin-bottom: -10px;
        }
    }

    .answer-product.spacer {
        grid-column: span 1;
    }

    .draft-mode {
        &.answer-products {
            background-image:
                linear-gradient(to right, rgba(0, 0, 0, 0.3) 1px, transparent 1px),
                linear-gradient(to bottom, rgba(0, 0, 0, 0.3) 1px, transparent 1px);
            background-size: 5% 50%;
        }

        .answer-product.spacer {
            background: rgba(0, 0, 0, 0.2);
        }
    }

    .answer-product.wide {
        grid-column: span 6;
    }

    .answer-product.tall {
        grid-column: span 3;
        grid-row: span 2;
        margin-top: -50%;

        & > img {
            width: auto;
            height: 100%;
            object-fit: contain;
        }
    }

    .answer-product:active {
        cursor: grabbing;
        transform: scale(1.05);
    }

    @media (hover: hover) {
        .answer-product:hover {
            transform: translateY(-6px);
            filter: drop-shadow(0 8px 16px rgba(0, 0, 0, 0.3));
        }
    }

    .answer-product.dragging {
        opacity: 0;
        transform: scale(0.9);
    }

    .answer-product.placed {
        opacity: 0;
        pointer-events: none;
        cursor: default;
    }
</style>
